import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Search, PresentationIcon, MoreVertical, 
  Users, Target, Building2, UserCircle, Trash2, Edit, Eye,
  Calendar, Clock
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSafeMode } from '../contexts/SafeModeContext';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import VisualizationCreateWizard from '../components/visualizations/VisualizationCreateWizard';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Audience icons mapping
const AUDIENCE_ICONS = {
  board: Target,
  team: Users,
  customer: Building2,
  custom: UserCircle,
};

const AUDIENCE_LABELS = {
  board: 'Board',
  team: 'Team',
  customer: 'Customer',
  custom: 'Custom',
};

const VisualizationsPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  
  const [visualizations, setVisualizations] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateWizard, setShowCreateWizard] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  const fetchVisualizations = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/visualizations`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setVisualizations(data);
      } else {
        toast.error('Failed to load visualizations');
      }
    } catch (error) {
      console.error('Error fetching visualizations:', error);
      toast.error('Failed to load visualizations');
    } finally {
      setLoading(false);
    }
  }, []);
  
  const fetchProducts = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/products`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  }, []);
  
  useEffect(() => {
    fetchVisualizations();
    fetchProducts();
  }, [fetchVisualizations, fetchProducts]);
  
  const handleDelete = async (vizId) => {
    try {
      const response = await fetch(`${API}/api/visualizations/${vizId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (response.ok) {
        toast.success('Visualization deleted');
        setVisualizations(prev => prev.filter(v => v.visualization_id !== vizId));
      } else {
        toast.error('Failed to delete visualization');
      }
    } catch (error) {
      console.error('Error deleting visualization:', error);
      toast.error('Failed to delete visualization');
    } finally {
      setDeleteConfirm(null);
    }
  };
  
  const handleCreateSuccess = (newViz) => {
    setShowCreateWizard(false);
    setVisualizations(prev => [newViz, ...prev]);
    // Navigate to workspace
    navigate(`/visualizations/${newViz.visualization_id}`);
  };
  
  // Filter visualizations by search
  const filteredVisualizations = visualizations.filter(viz =>
    viz.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (viz.product_names || []).some(name => 
      name.toLowerCase().includes(searchQuery.toLowerCase())
    )
  );
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };
  
  const hasProducts = products.length > 0;
  const hasVisualizations = visualizations.length > 0;
  
  return (
    <div className={cn("flex h-screen", colors.bgPrimary)}>
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header 
          title="Visualizations"
          subtitle="Create shareable roadmaps and timelines for your stakeholders"
          action={
            hasVisualizations && (
              <Button 
                onClick={() => setShowCreateWizard(true)}
                className="gap-2"
                disabled={!hasProducts}
                data-testid="create-visualization-btn"
              >
                <Plus className="w-4 h-4" />
                New Visualization
              </Button>
            )
          }
        />
        <main className={cn("flex-1 overflow-y-auto p-6", colors.bgPrimary)}>
          <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
            </div>
          ) : !hasVisualizations && !searchQuery ? (
            /* Empty State - Detailed Explainer */
            <div className={cn(
              "rounded-xl border-2 border-dashed p-8 md:p-12",
              colors.borderPrimary
            )}>
              <div className="max-w-3xl mx-auto">
                {/* Icon */}
                <div className="flex justify-center mb-6">
                  <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg">
                    <PresentationIcon className="w-10 h-10 text-white" />
                  </div>
                </div>
                
                {/* Title */}
                <h2 className={cn("text-2xl font-bold text-center mb-4", colors.textPrimary)}>
                  One Roadmap, Many Audiences
                </h2>
                
                {/* Problem Statement */}
                <p className={cn("text-center mb-8 leading-relaxed", colors.textSecondary)}>
                  Sharing product roadmaps is hard. Board members want strategic outcomes. 
                  Customers want release dates. Your team needs execution details. 
                  Same data, completely different stories - and keeping them all in sync is a nightmare.
                </p>
                
                {/* Feature Grid */}
                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <div className={cn("p-4 rounded-lg", colors.bgSecondary)}>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                        <Target className="w-4 h-4 text-purple-600" />
                      </div>
                      <h3 className={cn("font-semibold", colors.textPrimary)}>Audience-Tailored</h3>
                    </div>
                    <p className={cn("text-sm", colors.textMuted)}>
                      AI automatically adapts your roadmap content for boards, customers, or teams - same source data, audience-appropriate messaging.
                    </p>
                  </div>
                  
                  <div className={cn("p-4 rounded-lg", colors.bgSecondary)}>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                        <Users className="w-4 h-4 text-purple-600" />
                      </div>
                      <h3 className={cn("font-semibold", colors.textPrimary)}>Shareable & Embeddable</h3>
                    </div>
                    <p className={cn("text-sm", colors.textMuted)}>
                      Generate public links or embed visualizations directly into your website, portal, or presentations with a simple code snippet.
                    </p>
                  </div>
                  
                  <div className={cn("p-4 rounded-lg", colors.bgSecondary)}>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                        <Building2 className="w-4 h-4 text-purple-600" />
                      </div>
                      <h3 className={cn("font-semibold", colors.textPrimary)}>Branded & Professional</h3>
                    </div>
                    <p className={cn("text-sm", colors.textMuted)}>
                      Apply your organization&apos;s logo, colors, and styling. Create branded templates once and use them across all your visualizations.
                    </p>
                  </div>
                  
                  <div className={cn("p-4 rounded-lg", colors.bgSecondary)}>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                        <Clock className="w-4 h-4 text-purple-600" />
                      </div>
                      <h3 className={cn("font-semibold", colors.textPrimary)}>Always Up-to-Date</h3>
                    </div>
                    <p className={cn("text-sm", colors.textMuted)}>
                      One click to regenerate from latest product data. Version control lets you track changes and revert when needed.
                    </p>
                  </div>
                </div>
                
                {/* CTA */}
                <div className="text-center">
                  {hasProducts ? (
                    <Button 
                      onClick={() => setShowCreateWizard(true)}
                      size="lg"
                      className="gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                      data-testid="create-visualization-btn"
                    >
                      <Plus className="w-5 h-5" />
                      Create Your First Visualization
                    </Button>
                  ) : (
                    <Button 
                      onClick={() => navigate('/products/new')}
                      size="lg"
                      className="gap-2 bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      <Plus className="w-5 h-5" />
                      Create Your First Product to Visualize
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            /* List View with Search */
            <>
              {/* Search - only shown when there are visualizations */}
              {hasVisualizations && (
                <div className="relative max-w-md mb-6">
                  <Search className={cn("absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4", colors.textMuted)} />
                  <Input
                    placeholder="Search visualizations..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={cn("pl-10", colors.bgSecondary, colors.borderPrimary)}
                    data-testid="search-visualizations"
                  />
                </div>
              )}
              
              {filteredVisualizations.length === 0 && searchQuery ? (
                /* Search No Results */
                <div className="flex flex-col items-center justify-center h-64 text-center">
                  <div className={cn(
                    "w-16 h-16 rounded-full flex items-center justify-center mb-4",
                    colors.bgSecondary
                  )}>
                    <Search className={cn("w-8 h-8", colors.textMuted)} />
                  </div>
                  <h3 className={cn("text-lg font-medium mb-2", colors.textPrimary)}>
                    No visualizations found
                  </h3>
                  <p className={cn("text-sm mb-4", colors.textMuted)}>
                    Try adjusting your search terms
                  </p>
                  <Button 
                    variant="outline" 
                    onClick={() => setSearchQuery('')}
                    className="gap-2"
                  >
                    Clear Search
                  </Button>
                </div>
              ) : (
                /* Visualization Grid */
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredVisualizations.map((viz) => {
                    const AudienceIcon = AUDIENCE_ICONS[viz.target_audience] || UserCircle;
                    const hasPublished = !!viz.published_version_id;
                    
                    return (
                      <div
                        key={viz.visualization_id}
                        className={cn(
                          "border rounded-lg p-4 transition-all cursor-pointer group",
                          "hover:shadow-md hover:border-purple-500/50",
                          colors.bgSecondary,
                          colors.borderPrimary
                        )}
                        onClick={() => navigate(`/visualizations/${viz.visualization_id}`)}
                        data-testid={`viz-card-${viz.visualization_id}`}
                      >
                        {/* Card Header */}
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <div className={cn(
                              "w-8 h-8 rounded-lg flex items-center justify-center",
                              "bg-purple-100 dark:bg-purple-900/30"
                            )}>
                              <PresentationIcon className="w-4 h-4 text-purple-600" />
                            </div>
                            <div>
                              <h3 className={cn(
                                "font-medium text-sm line-clamp-1",
                                colors.textPrimary
                              )}>
                                {viz.name}
                              </h3>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <AudienceIcon className={cn("w-3 h-3", colors.textMuted)} />
                                <span className={cn("text-xs", colors.textMuted)}>
                                  {AUDIENCE_LABELS[viz.target_audience]}
                                </span>
                              </div>
                            </div>
                          </div>
                          
                          {/* Actions Dropdown */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-8 w-8 opacity-0 group-hover:opacity-100"
                              >
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/visualizations/${viz.visualization_id}`);
                                }}
                              >
                                <Edit className="w-4 h-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              {hasPublished && (
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(`/viz/${viz.share_token}`, '_blank');
                                  }}
                                >
                                  <Eye className="w-4 h-4 mr-2" />
                                  View Published
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteConfirm(viz);
                                }}
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                        
                        {/* Products */}
                        <div className="mb-3">
                          <p className={cn("text-xs mb-1", colors.textMuted)}>Products</p>
                          <p className={cn("text-sm line-clamp-1", colors.textSecondary)}>
                            {(viz.product_names || []).join(', ') || 'No products selected'}
                          </p>
                        </div>
                        
                        {/* Footer */}
                        <div className="flex items-center justify-between pt-3 border-t border-dashed" style={{ borderColor: 'var(--border)' }}>
                          <div className="flex items-center gap-1">
                            <Calendar className={cn("w-3 h-3", colors.textMuted)} />
                            <span className={cn("text-xs", colors.textMuted)}>
                              {formatDate(viz.updated_at)}
                            </span>
                          </div>
                          
                          {/* Status Badge */}
                          {hasPublished ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                              Published
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
                              Draft
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
          </div>
        </main>
      </div>
      
      {/* Create Wizard Modal */}
      {showCreateWizard && (
        <VisualizationCreateWizard
          onClose={() => setShowCreateWizard(false)}
          onSuccess={handleCreateSuccess}
        />
      )}
      
      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Visualization</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{deleteConfirm?.name}&quot;? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDelete(deleteConfirm.visualization_id)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default VisualizationsPage;
